import { getApiOrigin } from './api';
import { publishLiveEvent, setLiveConnection } from './liveRefresh';

/**
 * WebSocket client for real-time team/timesheet updates.
 * Connects to backend /ws/team/ and dispatches "teamDataInvalidated" when server sends refresh.
 */

const getAuthToken = () => {
  try {
    return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
  } catch (_) {
    return '';
  }
};

const getWsUrl = () => {
  const origin = getApiOrigin();
  const wsProtocol = origin.startsWith('https') ? 'wss:' : 'ws:';
  const hostAndPort = origin.replace(/^https?:\/\//, '');
  const wsBase = `${wsProtocol}//${hostAndPort}/ws/team/`;
  const token = getAuthToken();
  if (token) {
    const separator = wsBase.includes('?') ? '&' : '?';
    return `${wsBase}${separator}token=${encodeURIComponent(token)}`;
  }
  return wsBase;
};

const EVENT_REFRESH = 'refresh';
const CUSTOM_EVENT_NAME = 'teamDataInvalidated';

let socket = null;
let reconnectTimeout = null;
let pingInterval = null;
let reconnectAttempts = 0;
let shouldConnect = false;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_DELAY_MS = 2000;
const MAX_RECONNECT_DELAY_MS = 30000;
const PING_INTERVAL_MS = 25000;

const clearPingInterval = () => {
  if (pingInterval) {
    clearInterval(pingInterval);
    pingInterval = null;
  }
};

const startPingInterval = () => {
  clearPingInterval();
  pingInterval = setInterval(() => {
    if (socket?.readyState === WebSocket.OPEN) {
      try {
        socket.send(JSON.stringify({ type: 'ping' }));
      } catch (_) {
        // ignore send failures
      }
    }
  }, PING_INTERVAL_MS);
};

const connect = () => {
  const token = getAuthToken();
  if (!shouldConnect || !token || typeof WebSocket === 'undefined') return;
  if (
    socket?.readyState === WebSocket.CONNECTING
    || socket?.readyState === WebSocket.OPEN
    || socket?.readyState === WebSocket.CLOSING
  ) return;

  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }

  const url = getWsUrl();
  try {
    const nextSocket = new WebSocket(url);
    socket = nextSocket;
    nextSocket.onopen = () => {
      if (!shouldConnect || !getAuthToken()) {
        nextSocket.close();
        return;
      }
      reconnectAttempts = 0;
      setLiveConnection(true);
      startPingInterval();
      if (process.env.NODE_ENV === 'development') {
        console.debug('[ws] Connected to team updates');
      }
    };
    nextSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data?.type === EVENT_REFRESH || data?.event) {
          publishLiveEvent(data);
        }
        if (data?.type === 'pong') return;
      } catch (_) {
        // ignore non-JSON
      }
    };
    nextSocket.onclose = () => {
      setLiveConnection(false);
      clearPingInterval();
      if (socket === nextSocket) socket = null;
      if (!shouldConnect) return;

      const currentToken = getAuthToken();
      if (!currentToken || reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
        return;
      }

      const delay = (0.8 + Math.random() * 0.4) * Math.min(
        RECONNECT_DELAY_MS * Math.pow(1.5, reconnectAttempts),
        MAX_RECONNECT_DELAY_MS
      );
      reconnectAttempts += 1;
      reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        connect();
      }, delay);
    };
    nextSocket.onerror = () => {
      // onclose will handle reconnecting
    };
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[ws] Connect error', e);
    }
  }
};

const disconnect = () => {
  setLiveConnection(false);
  shouldConnect = false;
  clearPingInterval();
  if (reconnectTimeout) {
    clearTimeout(reconnectTimeout);
    reconnectTimeout = null;
  }
  reconnectAttempts = 0;
  if (socket?.readyState === WebSocket.OPEN) {
    socket.close();
  } else if (socket?.readyState === WebSocket.CLOSED) {
    socket = null;
  }
};

/**
 * Start WebSocket when user is authenticated. Call when app has token.
 */
const start = () => {
  if (!getAuthToken()) return;
  shouldConnect = true;
  reconnectAttempts = 0;
  connect();
};

/**
 * If we should be connected but aren't, reset the give-up budget and try again.
 * Used after a successful access-token refresh.
 */
const restartIfDisconnected = () => {
  if (!shouldConnect || !getAuthToken()) return;
  if (
    socket?.readyState === WebSocket.CONNECTING
    || socket?.readyState === WebSocket.OPEN
  ) return;
  reconnectAttempts = 0;
  connect();
};

/**
 * Stop WebSocket and cancel reconnects. Call on logout.
 */
const stop = () => {
  disconnect();
};

export const teamDataInvalidatedEventName = CUSTOM_EVENT_NAME;

const websocketService = {
  start,
  stop,
  restartIfDisconnected,
  getWsUrl,
};

export default websocketService;
